// src/components/dashboard/ActiveScoringWidget.js
// Updated: 11/01/2025 08:00 PM EDT
// --- THIS IS THE FULL, UPDATED FILE ---

import React, { useState, useEffect } from 'react';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  limit,
  // ADDED IMPORTS
  getDocs,
} from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { Award, ChevronRight, AlertCircle, Settings } from 'lucide-react';

// Small spinner for the widget
const WidgetSpinner = () => (
  <div className="flex h-32 items-center justify-center">
    <div className="h-6 w-6 animate-spin rounded-full border-2 border-dashed border-blue-500"></div>
  </div>
);

export default function ActiveScoringWidget() {
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const { userData, currentUser, isAuthLoading } = useAuth();
  
  // NEW STATE: Store completion info
  const [completionData, setCompletionData] = useState({}); 
  const [isProcessingScores, setIsProcessingScores] = useState(false);


  useEffect(() => {
    if (isAuthLoading || !userData?.orgId || !currentUser?.uid) {
      if (!isAuthLoading) setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');

    const isAdmin = userData.role === 'admin';
    const currentUserId = currentUser.uid;

    // --- UPDATED QUERY LOGIC (Fetch all relevant sessions for client-side filtering) ---
    const q = query(
      collection(db, 'scoringSessions'),
      where('orgId', '==', userData.orgId),
      // Query for sessions currently active or in setup
      where('status', 'in', ['open', 'setup']),
      orderBy('createdAt', 'desc'),
      limit(10)
    );
    // --- END UPDATED QUERY LOGIC ---


    const unsubscribe = onSnapshot(
      q,
      async (querySnapshot) => { 
        let sessionsData = [];
        querySnapshot.forEach((doc) => {
          sessionsData.push({ id: doc.id, ...doc.data() });
        });
        
        
        // --- START CLIENT-SIDE FILTERING ---
        // If not admin, filter results to ONLY those relevant to the user (Reviewer or Sponsor)
        if (!isAdmin) {
             sessionsData = sessionsData.filter(session =>
                 (session.reviewers?.includes(currentUserId)) ||
                 (session.sessionSponsors?.includes(currentUserId))
             );
        }
        // --- END CLIENT-SIDE FILTERING ---

        setSessions(sessionsData);
        setIsLoading(false);
        
        // --- Calculate completion status (Only for reviewers on 'open' sessions) ---
        setIsProcessingScores(true);
        const newCompletionData = {};
        
        await Promise.all(sessionsData.map(async (session) => {
            const isReviewer = session.reviewers?.includes(currentUserId);
            
            // Only calculate package/score counts if the session is open AND the user is a reviewer
            if (session.status === 'open' && isReviewer) {
                try {
                    // Get package count
                    const packagesQuery = query(collection(db, 'scoringSessions', session.id, 'packages'));
                    const packagesSnap = await getDocs(packagesQuery);
                    const pkgCount = packagesSnap.size;

                    // Get this user's score count
                    const scoresQuery = query(
                        collection(db, 'scoringSessions', session.id, 'scores'),
                        where('reviewerId', '==', currentUserId)
                    );
                    const scoresSnap = await getDocs(scoresQuery);
                    const scoreCount = scoresSnap.size;

                    newCompletionData[session.id] = { pkgCount, scoreCount };
                } catch (e) {
                    console.warn(`Widget failed to fetch completion data for session ${session.id}`, e);
                    newCompletionData[session.id] = { pkgCount: 0, scoreCount: 0 };
                }
            } else {
                 // For sessions not needing review (setup/closed or non-reviewer), set placeholder
                 newCompletionData[session.id] = { pkgCount: -1, scoreCount: -1 }; 
            }
        }));

        setCompletionData(newCompletionData);
        setIsProcessingScores(false);
      },
      (err) => {
        console.error('Error fetching scoring for widget:', err);
        setError('Could not load scoring sessions.');
        setIsLoading(false);
        setIsProcessingScores(false);
      }
    );

    return () => unsubscribe();
  }, [userData, currentUser, isAuthLoading]);
  
  // Filter out completed sessions for members/reviewers
  const filteredSessions = sessions.filter(session => {
    const currentUserId = currentUser?.uid;
    const isGlobalAdmin = userData.role === 'admin';
    const isSponsor = session.sessionSponsors?.includes(currentUserId);
    const isReviewer = session.reviewers?.includes(currentUserId);
    const data = completionData[session.id];
    
    // Managers (Admin/Sponsor) always see the session if it's 'setup' or 'open'
    // They are responsible for setup/closing/management even if they finished their review.
    if (isGlobalAdmin || isSponsor) {
        return true;
    }
    
    // For standard reviewers (members/reviewers), filter out completed sessions
    if (session.status === 'open' && isReviewer) {
        // Only hide if the data is loaded and is complete
        if (data && data.pkgCount > 0 && data.scoreCount === data.pkgCount) {
            return false; // Hide completed sessions from reviewer's active list
        }
    }
    
    // Default to showing the session
    return true; 
  });


  // Helper to determine the correct link and action text
  const getSessionLink = (session) => {
    const isAdmin = userData.role === 'admin';
    // FIX: Use session-specific sponsor check
    const isSponsor = session.sessionSponsors?.includes(currentUser?.uid); 
    const isReviewer = session.reviewers?.includes(currentUser?.uid);
    // FIX: Determine management rights for *this* session
    const canManage = isAdmin || isSponsor; 
    const data = completionData[session.id] || {};

    // 1. Management links (Setup or Open)
    if (canManage) {
        const link = `/admin/scoring/${session.id}/manage`;
        
        if (session.status === 'setup') {
             return { to: link, text: 'Setup Session', icon: <Settings className="h-4 w-4 text-yellow-500" /> };
        }
        
        // If manager is also a reviewer, show their scoring status, otherwise show manage
        // We always show Manage Session link for managers (Admin/Sponsor) if the session is open.
        // The reviewer link is displayed on the main ScoringPage, but here we prioritize management.
        if (session.status === 'open') {
             return { to: link, text: 'Manage Session', icon: <Settings className="h-4 w-4 text-gray-500" /> };
        }
    }

    // 2. Reviewer links (Open status only)
    if (session.status === 'open' && isReviewer) {
        let text = 'Start Scoring';
        if (data.pkgCount > 0) {
            text = `Scoring (${data.scoreCount}/${data.pkgCount})`;
        }
        // Note: Completed sessions are already filtered out by filteredSessions.
        return { to: `/scoring/${session.id}`, text: text, icon: <Award className="h-4 w-4 text-green-500" /> };
    }
    
    // Fallback
    return { to: '/scoring', text: 'View All', icon: <ChevronRight className="h-5 w-5" /> };
  };

  const showLoading = isLoading || isProcessingScores;

  return (
    <div className="rounded-lg bg-white p-6 shadow-lg h-full">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800">Active Scoring</h2>
        <Award className="h-6 w-6 text-green-500" />
      </div>
      <p className="mt-1 text-sm text-gray-500">Sessions that require your review or setup.</p>

      <div className="mt-4">
        {showLoading ? (
          <WidgetSpinner />
        ) : error ? (
          <div className="flex h-32 items-center justify-center text-sm text-red-600">
            <AlertCircle className="mr-2 h-4 w-4" /> {error}
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="flex h-32 items-center justify-center">
            <p className="text-center text-gray-500">
              All caught up!
              <br />
              You have no pending scoring sessions.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {filteredSessions.map((session) => {
              const { to, text, icon } = getSessionLink(session);
              return (
                <li key={session.id} className="py-3">
                  <Link
                    to={to}
                    className="group flex items-center justify-between"
                  >
                    <div>
                      <p className="font-medium text-gray-900 group-hover:text-blue-600">
                        {session.name}
                      </p>
                      <div className="flex items-center text-sm text-gray-500 group-hover:text-blue-600">
                        {icon}
                        <span className="ml-1.5">{text}</span>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-blue-600" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
